/**
 * HTTP client shared by the web app and the native app.
 *
 * Adapted from Flugvorbereitung's shared/src/api.ts. Everything platform
 * specific is injected: where the API lives and where the session token is
 * kept. The token is mirrored in memory so that reading it is synchronous on
 * both platforms (the native keychain is async, and a render pass cannot await).
 *
 * The token travels in `Authorization: Bearer`. This project calls a separate
 * Function App cross-origin, so there is no SWA proxy that would replace the
 * header; the custom-header workaround from Flugvorbereitung is not needed.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly retryAfterMs?: number,
    /** Machine-readable reason from the body (`forbidden_field`, `exif`, …), when the server sends one. */
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Where the session token survives a restart. Async, because keychains are. */
export interface TokenStore {
  load(): Promise<string | null>;
  save(token: string): Promise<void>;
  clear(): Promise<void>;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Abort signal, so a screen leaving does not leave a request running. */
  signal?: AbortSignal;
  /** Send without the session token (login endpoints, public health). */
  anonymous?: boolean;
  /** Overrides the client's timeout, for the few calls that may run long (exports). */
  timeoutMs?: number;
}

export interface ApiClient {
  <T>(path: string, options?: RequestOptions): Promise<T>;
  getToken(): string | null;
  setToken(token: string): void;
  clearToken(): void;
  /** Reads the persisted token into memory. Await before the first render. */
  hydrate(): Promise<void>;
  /** Called after the server answered 401 and the token was dropped. */
  onUnauthorized(listener: () => void): () => void;
  readonly baseUrl: string;
}

export interface ApiClientOptions {
  /** Absolute origin of the API including the /api prefix, e.g. https://api-fsv.apps.d-vo.de/api */
  baseUrl: string;
  store: TokenStore;
  /**
   * Token already known at construction time. The web reads localStorage
   * synchronously and passes it here so the very first render is signed in;
   * the native app leaves it empty and awaits hydrate() behind its splash.
   */
  initialToken?: string | null;
  /** Message for a failure that never reached the server. */
  offlineMessage?: string;
  /** Injected for tests; defaults to the global fetch. */
  fetchImpl?: typeof fetch;
  /**
   * How long a request may take before it counts as „keine Verbindung“
   * (status 0). Without it, a stalled connection at the airfield kept a
   * screen's spinner turning for good (owner, 08.10.2026, Eingang).
   */
  timeoutMs?: number;
}

/** Default for timeoutMs: long enough for a slow mobile link, short enough to give up. */
export const DEFAULT_TIMEOUT_MS = 30_000;

interface ErrorBody {
  error?: string;
  code?: string;
  retryAfterMs?: number;
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const { baseUrl, store } = options;
  // Looked up per call, not captured: a test that stubs the global fetch must see every request.
  const fetchImpl: typeof fetch = options.fetchImpl ?? ((input, init) => fetch(input, init));
  const offlineMessage =
    options.offlineMessage ?? 'Keine Verbindung. Bitte später erneut versuchen.';
  const defaultTimeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  let token: string | null = options.initialToken ?? null;
  // A request must never be the first thing to run on a client that has not
  // read its store: a stale "signed in" state would send no token, get a 401,
  // and clear the keychain for good. Hydrate lazily on the first request.
  let hydrated = options.initialToken != null;
  const unauthorizedListeners = new Set<() => void>();

  const client = async function request<T>(
    path: string,
    requestOptions: RequestOptions = {},
  ): Promise<T> {
    if (!hydrated) await client.hydrate();
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token && !requestOptions.anonymous) headers['Authorization'] = `Bearer ${token}`;

    let body: string | undefined;
    if (requestOptions.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(requestOptions.body);
    }

    // Our own controller: it carries the timeout and follows the caller's signal.
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, requestOptions.timeoutMs ?? defaultTimeoutMs);
    const outer = requestOptions.signal;
    const follow = () => controller.abort();
    if (outer?.aborted) controller.abort();
    else outer?.addEventListener('abort', follow);

    let res: Response;
    let data: ErrorBody & T;
    try {
      try {
        const init: RequestInit = { method: requestOptions.method ?? 'GET', headers, signal: controller.signal };
        if (body !== undefined) init.body = body;
        res = await fetchImpl(`${baseUrl}${path}`, init);
      } catch (err) {
        // A timeout is a connection that never answered: offline, like a failed fetch. The caller's own abort stays an abort.
        if (!timedOut && err instanceof Error && err.name === 'AbortError') throw err;
        throw new ApiError(0, offlineMessage);
      }

      if (res.status === 401 && !requestOptions.anonymous && token) {
        client.clearToken();
        for (const listener of unauthorizedListeners) listener();
      }
      if (res.status === 204) return undefined as T;

      // The body counts toward the timeout too: a stalled download is no answer either.
      data = (await res.json().catch(() => {
        if (timedOut) throw new ApiError(0, offlineMessage);
        return {};
      })) as ErrorBody & T;
    } finally {
      clearTimeout(timer);
      outer?.removeEventListener('abort', follow);
    }
    if (!res.ok)
      throw new ApiError(
        res.status,
        data.error ?? `Fehler (${res.status})`,
        data.retryAfterMs,
        typeof data.code === 'string' ? data.code : undefined,
      );
    return data;
  } as ApiClient;

  Object.defineProperty(client, 'baseUrl', { value: baseUrl, writable: false });
  client.getToken = () => token;
  client.setToken = (value: string) => {
    hydrated = true;
    token = value;
    void store.save(value).catch(() => undefined);
  };
  client.clearToken = () => {
    token = null;
    void store.clear().catch(() => undefined);
  };
  client.hydrate = async () => {
    hydrated = true;
    if (token) return;
    token = await store.load().catch(() => null);
  };
  client.onUnauthorized = (listener) => {
    unauthorizedListeners.add(listener);
    return () => unauthorizedListeners.delete(listener);
  };

  return client;
}
